import {
  Box,
  Button,
  chakra,
  Flex,
  Image,
  shouldForwardProp,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { parseSomething } from "../../utils/parseSomething";
import { useDispatch, useSelector } from "react-redux";
import { isValidMotionProp, motion, AnimatePresence } from "framer-motion";
import { COMMON_WEB_PATH } from "../../constant";
import Count from "./components/Count";
import dayjs from "dayjs";
import useSound from "../../hook/useSound";
import {
  CLEAR_SOCKET_MESSAGE,
  notice,
  SAVE_SOCKET_MESSAGE,
} from "../../redux/action/action";
import { useTranslation } from "react-i18next";
import {
  SHOW_MAINTAIN_ALERT,
  storeCurrencyExchange,
  triggerTransferGivenUpPoint,
  TRANSFER_READY,
} from "../../redux/action/member/memberAction";
import earningMusic from "../../images/music/earning-money.mp3";
import useCheckLogin from "../../hook/useCheckLogin";
import CommonModal from "../../components/Common/CommonModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTransferInGameHandler } from "../Game/components/TransferInGame/transferInGameHandler";
import DevTestPanel from "./components/DevTestPanel";
import { handleRemoteControl } from "../../utils/remoteControl";
// import TestDepositButton from "./components/TestDepositButton";

const gradientStyle = {
  background:
    "linear-gradient(180deg, rgba(2,0,36,1) 0%, rgba(247,249,79,1) 0%, rgba(254,238,54,1) 50%, rgba(245,174,12,1) 75%, rgba(236,208,59,1) 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const animationVariants = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1.5,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 20,
      mass: 0.5,
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
  exit: {
    scale: 0,
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

const HEARTBEAT_INTERVAL = 30000;

const EarnMoneyFromSocket = () => {
  const { t } = useTranslation();
  const i18n_common = (key, props) => t(`common.${key}`, { ...props });
  const i18n_backend = (key) => t(`backend_response.${key}`);

  const toast = useToast();

  const [socketUrl, setSocketUrl] = useState(process.env.REACT_APP_WS_HOST);
  const [messages, setMessages] = useState([]);
  const [testWsData, setTestWsData] = useState(null); // 測試用：模擬 WS 訊息
  const messageIdRef = useRef(0);
  const generateMessageId = () => `${Date.now()}-${messageIdRef.current++}`;

  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const memberInfo = useSelector((state) => state.isMemberInfo);
  const isSocketMessage = useSelector((state) => state.isSocketMessage);
  const isTransferInGameState = useSelector(
    (state) => state.isTransferInGameState
  );
  const gameConfig = useSelector((state) => state.isGameConfig);

  const { message, show, rawMessage } = isSocketMessage || {};

  const { handleTransferAllIn } = useTransferInGameHandler();

  const { uid, cagent_uid } = memberInfo || {};

  const currentGameUid = gameConfig?.gameUid ?? null;
  const { isLogin } = useCheckLogin();

  const earningSound = useSound({ customSound: "earning_money" });

  const didUnmount = useRef(false);


  // 記錄已處理過的 ws 訊息時間戳，避免換頁造成依賴變動時同一則訊息被重複處理

  const processedTimeStampRef = useRef(null);

  const {
    sendMessage,
    sendJsonMessage,
    lastMessage,
    lastJsonMessage,
    readyState,
    getWebSocket,
  } = useWebSocket(socketUrl, {
    queryParams: {
      key: "wsky",
    },
    onOpen: () => console.log("連線成功"),
    shouldReconnect: (closeEvent) => {
      /*
      useWebSocket will handle unmounting for you, but this is an example of a 
      case in which you would not want it to automatically reconnect
    */
      return didUnmount.current === false;
    },
    reconnectAttempts: 9999,
    reconnectInterval: 1000,
  });

  const socketReceivedData = useMemo(() => {
    if (lastMessage?.data) {
      return parseSomething(lastMessage.data);
    }
  }, [lastMessage?.timeStamp]);

  const compareMemberData = useMemo(() => {
    if (socketReceivedData?.member_info_uid == uid) {
      return true;
    }
    return false;
  }, [socketReceivedData, uid]);

  const handleConfirm = () => {
    dispatch(CLEAR_SOCKET_MESSAGE());
  };

  const handleCloseMessage = (id) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  const responseSocketModal = useMemo(() => {
    if (show) {
      return (
        <CommonModal bgNone>
          <Box fontSize="24px" color="white" textAlign="center" mb={2}>
            {rawMessage ? message : i18n_backend(message)}
          </Box>
          <Flex
            gap={4}
            mb={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              sx={{
                bgImage: `url(${COMMON_WEB_PATH}/theOne/button/confirm_background.png)`,
                bgSize: "contain",
                bgRepeat: "no-repeat",
                bgPosition: "center",
                background: "transparent",
                _hover: {
                  bgImage: `url(${COMMON_WEB_PATH}/theOne/button/confirm_background.png)`,
                },
                _active: {
                  bgImage: `url(${COMMON_WEB_PATH}/theOne/button/confirm_background.png)`,
                },
                h: "65px",
              }}
              onClick={handleConfirm}
              py="20px"
              type="submit"
              w="130px"
            >
              <Text mb="5px" color="#0C5426" fontWeight={900} fontSize="23px">
                {i18n_common("confirm")}
              </Text>
            </Button>
          </Flex>
        </CommonModal>
      );
    }
  }, [show, message]);

  const responseComponents = (
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Flex direction={"column"} gap="10px" alignItems={"center"}>
          <AnimatePresence>
          {messages?.map((msg) => {
            const { amount = 0, id } = msg;
            return (
              <ChakraBox
                key={id}
                variants={animationVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                bg={"rgba(0,0,0,0.5)"}
                px="30px"
                py="10px"
                borderRadius={"common"}
                bgRepeat="no-repeat"
                bgSize="100% 100%"
                bgPosition="center"
                position="relative"
              >
                <Box
                  as="button"
                  position="absolute"
                  top="6px"
                  right="10px"
                  color="white"
                  fontSize="20px"
                  lineHeight="1"
                  cursor="pointer"
                  onClick={() => handleCloseMessage(id)}
                  zIndex={1}
                >
                  ✕
                </Box>
                <Flex
                  gap="10px"
                  alignItems="center"
                  fontSize="90px"
                  fontWeight={900}
                  {...gradientStyle}
                >
                  <Text>+</Text>
                  <Text>
                    <Count from={0} to={Number(amount)} />
                  </Text>
                </Flex>
              </ChakraBox>
            );
          })}
          </AnimatePresence>
        </Flex>{" "}
      </Box>
  );

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    if (socketReceivedData) {
      // 每則 ws 訊息只處理一次；換頁造成的依賴變動不應重跑同一則(避免上分動畫重複)
      if (lastMessage?.timeStamp === processedTimeStampRef.current) return;
      processedTimeStampRef.current = lastMessage?.timeStamp;
      const { type, member_info_uid, data, memo } = socketReceivedData;
      /* 後台遠端關鎖 / 解鎖機台(黑屏),見 utils/remoteControl.js */
      if (handleRemoteControl(socketReceivedData, memberInfo?.machine_number)) return;
      if (type === "deposit" && member_info_uid === uid && data != null) {
        const { amount, exchange } = data || {};
        if (amount == "error") {
          return dispatch(
            notice({
              title: i18n_backend("Server Error"),
              type: "error",
            })
          );
        }
        dispatch(storeCurrencyExchange(exchange || {}));
        /**
         * @description 當收到存款的訊息時，會觸發all-in-la API
         */
        handleTransferAllIn();

        new Audio(earningMusic).play();

        const messageWithId = {
          amount: amount,
          id: generateMessageId(),
        };
        setMessages((prev) => [...prev, messageWithId]);

        setTimeout(() => {
          setMessages((prev) => prev.filter((msg) => msg.id !== messageWithId.id));
        }, 4000);
      } else if (
        type === "transCode" &&
        member_info_uid === uid &&
        data != null
      ) {
        if (data == "fails") {
          dispatch(
            SAVE_SOCKET_MESSAGE({
              message: memo,
              show: true,
            })
          );
        } else {
          dispatch(CLEAR_SOCKET_MESSAGE());
          dispatch(triggerTransferGivenUpPoint(true));

          setTimeout(() => {
            //RESET
            dispatch(triggerTransferGivenUpPoint(false));
          }, 5000);
        }
      } else if (type === "error" && member_info_uid === uid && data != null) {
        dispatch(
          SAVE_SOCKET_MESSAGE({
            message: data,
            show: true,
          })
        );
      } else if (type === "update_member") {
        if (isLogin) {
          dispatch({ type: "GET_MEMBER_DATA" });
        }
      } else if (type === "maintain_notice") {
        dispatch(
          SHOW_MAINTAIN_ALERT({
            state: true,
            maintainTime: data,
          })
        );
      } else if (type === "maintain_notice_member") {
        if (data?.cagent_uid != null) {
          if (String(cagent_uid) === String(data.cagent_uid)) {
            dispatch(
              SAVE_SOCKET_MESSAGE({
                message: data.msg,
                show: true,
                rawMessage: true,
              })
            );
            setTimeout(() => {
              navigate("/");
            }, 5000);
          }
          return;
        } else if (Number(member_info_uid) === Number(uid)) {
          if (data?.game_id != null) {
            if (
              Array.isArray(data.game_id) &&
              data.game_id.some((id) => String(id) === String(currentGameUid))
            ) {
              dispatch(
                SAVE_SOCKET_MESSAGE({
                  message: data.msg,
                  show: true,
                  rawMessage: true,
                })
              );
              setTimeout(() => {
                navigate("/");
              }, 5000);
            }
            return;
          }

          dispatch(
            SAVE_SOCKET_MESSAGE({
              message: data,
              show: true,
            })
          );
          setTimeout(() => {
            window.location.reload();
          }, 50000);
        }
      } else if (type == "reload") {
        window.location.reload();
      } else if (type == "transfer_in_game" || type == "CHECK_RREQUEST") {
        const { request_id, amount, message, platform, status } = data || {};
        if (member_info_uid !== uid) return;

        const current_request_id = localStorage.getItem(
          "transfer_in_game_request_id"
        );

        if (status === "pending") {
          localStorage.setItem("transfer_in_game_request_id", request_id);
          toast({
            title: i18n_common("提醒您"),
            description: i18n_common(
              "轉入處理中，請耐心等候，我們立即為您處理"
            ),
            status: "warning",
            duration: 7000,
            isClosable: true,
            position: "top",
          });
          return;
        } else {
          if (current_request_id == request_id) {
            if (status === "rollback") {
              handleTransferAllIn();
              return;
            }
            localStorage.removeItem("transfer_in_game_request_id");
            dispatch(TRANSFER_READY());
            toast({
              title: i18n_common("提醒您"),
              description: i18n_common(status, {
                amount: amount,
                platform: platform,
                status: status,
              }),
              status: "success",
              duration: 7000,
              isClosable: true,
              position: "top",
            });
          }
        }
      } else if (type === "update_only_one_member") {
        if (isLogin) {
          if (Number(member_info_uid) === uid) {
            dispatch({ type: "GET_MEMBER_DATA" });
          }
        }
      }
    }
  }, [socketReceivedData, uid]);

  useEffect(() => {
    const heartbeat = setInterval(() => {
      sendMessage("ping");
    }, HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(heartbeat);
    };
  }, [sendMessage]);
  useEffect(() => {
    /**
     * @description 這邊是處理遊戲中轉點的邏輯，發生於101,102
     * 用於當用戶重整時，會將lcoalStorage中的轉點請求單號傳給後端，後端會根據單號回傳結果
     * 邏輯的結果處理依照WebSocket回傳的type來決定
     */
    const current_request_id = localStorage.getItem(
      "transfer_in_game_request_id"
    );

    if (current_request_id && uid && isTransferInGameState === "pending") {
      sendJsonMessage({
        type: "CHECK_RREQUEST",
        request_id: current_request_id,
        member_info_uid: uid,
      });
    }
  }, [uid, isTransferInGameState]);
  const handleTestDeposit = () => {
    const testAmount = 888;
    const messageWithId = {
      amount: testAmount,
      id: generateMessageId(),
    };
    setMessages((prev) => [...prev, messageWithId]);
    setTimeout(() => {
      setMessages((prev) => prev.filter((msg) => msg.id !== messageWithId.id));
    }, 4000);
  };

  // 測試用：模擬一筆 WS 存款訊息（走與真實相同的 effect 依賴與去重守衛）
  const handleSimulateWsDeposit = (amount = 321) => {
    setTestWsData({ amount, key: `test-ws-${Date.now()}` });
  };

  // 測試用 effect：沿用本站真實 effect 的依賴 [socketReceivedData, uid]，共用 processedTimeStampRef 守衛
  useEffect(() => {
    if (!testWsData) return;
    if (testWsData.key === processedTimeStampRef.current) return;
    processedTimeStampRef.current = testWsData.key;
    new Audio(earningMusic).play();
    const messageWithId = { amount: testWsData.amount, id: generateMessageId() };
    setMessages((prev) => [...prev, messageWithId]);
    setTimeout(() => {
      setMessages((prev) => prev.filter((msg) => msg.id !== messageWithId.id));
    }, 4000);
  }, [testWsData, uid]);

  return (
    <Box zIndex={999999} position={"relative"}>
      {responseComponents}
      {responseSocketModal}

      {/* 統一測試面板:僅測試環境顯示(正式站自動隱藏)。
          這站的 WS 處理寫在 useEffect 裡、還沒抽成 processSocketMessage,
          所以不傳 onProcess → WS 類按鈕停用;情境測試(連點偵測)、上分動畫、後端訊息 Toast 可用 */}
      <DevTestPanel uid={uid} onSimDeposit={handleSimulateWsDeposit} />
      {/* 測試用：手動觸發存款動畫，需要時再開啟 */}
      {/* <TestDepositButton onTest={handleTestDeposit} onSimWs={handleSimulateWsDeposit} /> */}
    </Box>
  );
};

export default EarnMoneyFromSocket;
